
import * as Dialog from "@radix-ui/react-dialog";
import { Ellipsis, KeyRound, LogOut, Monitor, Moon, Sun, X } from "lucide-react";
import { Link } from "../router";
import { useRef } from "react";
import { useTranslation } from "react-i18next";

import { setAdminLocale } from "../../i18n-provider";
import { ChoiceGroup } from "./ChoiceGroup";
import { NAV_GROUPS, PHONE_PRIMARY_ITEMS, isActiveHref, visibleNavItems } from "./nav";
import { NavLink, PendingLabel } from "./NavLink";
import { setThemePreference, useTheme, type ThemePreference } from "./preferences";
import styles from "./shell.module.css";
import { useFocusHandoff } from "./use-focus-handoff";

type Props = {
  pathname: string;
  role: string | null;
  moreOpen: boolean;
  onMoreOpenChange: (open: boolean) => void;
  onSignOut: () => void;
};

const CHOICE_ICON = { className: styles.navIcon, strokeWidth: 1.75, "aria-hidden": true } as const;

export function BottomBar({ pathname, role, moreOpen, onMoreOpenChange, onSignOut }: Props) {
  const { t, i18n } = useTranslation();
  const { preference } = useTheme();
  const moreButtonRef = useRef<HTMLButtonElement | null>(null);
  const focusHandoff = useFocusHandoff();
  const items = visibleNavItems(role);
  const primaryActive = PHONE_PRIMARY_ITEMS.some((item) => isActiveHref(pathname, item.href));
  const close = () => onMoreOpenChange(false);

  return (
    <nav className={styles.bottomBar} aria-label={t("nav.shell.primaryNavigation")}>
      <ul className={styles.bottomList}>
        {PHONE_PRIMARY_ITEMS.map((item) => {
          const Icon = item.icon;
          const active = isActiveHref(pathname, item.href);
          return (
            <li key={item.id}>
              <Link
                href={item.href}
                className={styles.bottomItem}
                aria-current={active ? "page" : undefined}
              >
                <Icon className={styles.bottomIcon} strokeWidth={1.75} aria-hidden="true" />
                <PendingLabel className={styles.bottomLabel}>{t(`nav.short.${item.id}`)}</PendingLabel>
              </Link>
            </li>
          );
        })}
        <li>
          <Dialog.Root open={moreOpen} onOpenChange={onMoreOpenChange}>
            <Dialog.Trigger asChild>
              <button
                ref={moreButtonRef}
                type="button"
                className={styles.bottomItem}
                data-active={!primaryActive}
              >
                <Ellipsis className={styles.bottomIcon} strokeWidth={1.75} aria-hidden="true" />
                <span className={styles.bottomLabel}>{t("nav.short.more")}</span>
              </button>
            </Dialog.Trigger>
            <Dialog.Portal>
              <Dialog.Overlay className={styles.blanket} />
              <Dialog.Content className={styles.sheet} onCloseAutoFocus={focusHandoff.onCloseAutoFocus}>
                <span className={styles.sheetHandle} aria-hidden="true" />
                <div className={styles.sheetHeader}>
                  <Dialog.Title className={styles.sheetTitle}>{t("nav.shell.moreTitle")}</Dialog.Title>
                  <Dialog.Close asChild>
                    <button type="button" className={`${styles.iconButton} ${styles.userButtonCompact}`} aria-label={t("nav.shell.close")}>
                      <X className={styles.navIcon} strokeWidth={1.75} aria-hidden="true" />
                    </button>
                  </Dialog.Close>
                </div>
                <Dialog.Description className={styles.srOnly}>{t("nav.shell.moreDescription")}</Dialog.Description>
                <div className={styles.sheetBody}>
                  <nav aria-label={t("common.adminSections")}>
                    {NAV_GROUPS.map((group) => (
                      <section key={group} className={styles.sheetSection} aria-labelledby={`sheet-group-${group}`}>
                        <h2 id={`sheet-group-${group}`} className={styles.sheetGroupLabel}>{t(`nav.group.${group}`)}</h2>
                        <ul className={styles.sheetList}>
                          {items.filter((item) => item.group === group).map((item) => (
                            <li key={item.id}>
                              <NavLink item={item} active={isActiveHref(pathname, item.href)} withTooltip={false} onNavigate={close} />
                            </li>
                          ))}
                        </ul>
                      </section>
                    ))}
                  </nav>
                  <div className={styles.sheetSettings}>
                    <ChoiceGroup<ThemePreference>
                      label={t("nav.shell.theme")}
                      value={preference}
                      onChange={setThemePreference}
                      choices={[
                        { value: "light", label: t("nav.shell.themeLight"), icon: <Sun {...CHOICE_ICON} /> },
                        { value: "dark", label: t("nav.shell.themeDark"), icon: <Moon {...CHOICE_ICON} /> },
                        { value: "system", label: t("nav.shell.themeSystem"), icon: <Monitor {...CHOICE_ICON} /> },
                      ]}
                    />
                    <ChoiceGroup<string>
                      label={t("nav.shell.language")}
                      value={i18n.language}
                      onChange={setAdminLocale}
                      choices={[
                        { value: "pt-BR", label: t("nav.shell.languagePortuguese") },
                        { value: "en-US", label: t("nav.shell.languageEnglish") },
                      ]}
                    />
                    {role !== null ? (
                      <Link
                        href="/profile"
                        className={styles.sheetAction}
                        onClick={() => {
                          focusHandoff.handOff();
                          close();
                        }}
                      >
                        <KeyRound className={styles.navIcon} strokeWidth={1.75} aria-hidden="true" />
                        {t("nav.shell.security")}
                      </Link>
                    ) : null}
                    <button
                      type="button"
                      className={styles.signOut}
                      onClick={() => {
                        close();
                        onSignOut();
                      }}
                    >
                      <LogOut className={styles.navIcon} strokeWidth={1.75} aria-hidden="true" />
                      {t("nav.shell.signOut")}
                    </button>
                  </div>
                </div>
              </Dialog.Content>
            </Dialog.Portal>
          </Dialog.Root>
        </li>
      </ul>
    </nav>
  );
}
